// src/modules/Admin/pages/Jobs/JobScriptEditor.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../../../config/supabase';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  FaArrowLeft,
  FaSave,
  FaSpinner,
  FaCheck,
  FaBook,
  FaClock,
  FaDesktop,
  FaChartLine,
  FaFileContract,
  FaLink,
  FaQuestionCircle,
  FaEye
} from 'react-icons/fa';

// Styled Components
const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  text-decoration: none;
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 200ms;

  &:hover {
    background: #f5f5f5;
    color: #111;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #111;
`;

const JobTitle = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #666;
  display: block;
  margin-top: 4px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;

  ${props => props.$primary ? `
    background: #111;
    color: white;
    border: none;
    &:hover:not(:disabled) { background: #333; }
  ` : `
    background: white;
    color: #666;
    border: 1px solid #e5e5e5;
    &:hover { background: #f5f5f5; color: #111; }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    font-size: 16px;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e5e5;
  overflow-x: auto;
`;

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 20px;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.$active ? '#111' : '#666'};
  cursor: pointer;
  position: relative;
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props => props.$active ? '#111' : 'transparent'};
  }

  &:hover {
    color: #111;
  }

  svg {
    font-size: 16px;
  }
`;

const TabContent = styled.div`
  padding: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  svg {
    font-size: 20px;
    color: #666;
  }

  h2 {
    font-size: 18px;
    font-weight: 600;
    color: #111;
  }
`;

const SectionDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
  line-height: 1.5;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 300px;
  padding: 16px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.6;
  resize: vertical;
  transition: all 200ms;

  &:focus {
    outline: none;
    border-color: #111;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }

  &::placeholder {
    color: #999;
  }
`;

const HelpText = styled.p`
  font-size: 12px;
  color: #999;
  margin-top: 8px;

  code {
    background: #f5f5f5;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: monospace;
  }
`;

const SavedBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #10b981;
  font-size: 13px;
  font-weight: 500;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #666;

  svg {
    animation: spin 1s linear infinite;
    margin-right: 10px;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 60px 20px;

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
    color: #666;
    margin-bottom: 24px;
  }
`;

// Configuração das seções
const SECTIONS = [
  {
    key: 'overview',
    label: 'Visão Geral',
    icon: FaBook,
    description: 'Descrição geral da função, responsabilidades e expectativas.',
    placeholder: `# Bem-vindo à Vega Robotics!

## Sobre sua função
Você foi selecionado para atuar como [cargo] na equipe Vega...

## Suas responsabilidades
- Responsabilidade 1
- Responsabilidade 2
- Responsabilidade 3

## O que esperamos de você
...`
  },
  {
    key: 'daily_routine',
    label: 'Rotina Diária',
    icon: FaClock,
    description: 'Passo a passo de como deve ser o dia a dia de trabalho.',
    placeholder: `# Rotina Diária

## Início do dia (8h-9h)
1. Verificar emails e mensagens
2. Revisar tarefas pendentes
3. Planejar atividades do dia

## Durante o dia (9h-17h)
- Executar prospecções
- Registrar atividades no CRM
- Fazer follow-ups

## Final do dia (17h-18h)
- Atualizar status dos leads
- Reportar resultados
- Preparar próximo dia`
  },
  {
    key: 'platform_guide',
    label: 'Plataforma',
    icon: FaDesktop,
    description: 'Como utilizar o CRM, ferramentas e sistemas da empresa.',
    placeholder: `# Guia da Plataforma

## Acesso ao sistema
1. Acesse: admin.vegarobotics.com.br
2. Faça login com seu email Google cadastrado

## Navegação
- **Dashboard**: Visão geral das suas métricas
- **Leads**: Gerenciar seus leads no pipeline
- **Analytics**: Acompanhar desempenho

## Como usar o Pipeline
1. Arraste os cards entre as colunas
2. Clique em um lead para ver detalhes
3. Adicione notas de cada interação

## Dicas importantes
- Mantenha o CRM sempre atualizado
- Registre todas as interações`
  },
  {
    key: 'goals_metrics',
    label: 'Metas',
    icon: FaChartLine,
    description: 'Metas de desempenho, KPIs e como são mensurados.',
    placeholder: `# Metas e Métricas

## Metas Mensais
| Métrica | Meta | Bonificação |
|---------|------|-------------|
| Leads contatados | 100 | - |
| Reuniões agendadas | 20 | R$ 50/extra |
| Propostas enviadas | 10 | R$ 100/extra |
| Vendas fechadas | 3 | Comissão |

## Como é calculada sua comissão
- 5% sobre vendas de até R$ 5.000
- 7% sobre vendas de R$ 5.001 a R$ 15.000
- 10% sobre vendas acima de R$ 15.000

## Período de avaliação
- Fechamento mensal no dia 25
- Pagamento até dia 5 do mês seguinte`
  },
  {
    key: 'contract_terms',
    label: 'Contrato',
    icon: FaFileContract,
    description: 'Termos do contrato, regras e políticas.',
    placeholder: `# Termos do Contrato

## Tipo de Contratação
Prestador de Serviços (PJ/MEI) ou Autônomo

## Remuneração
- Modelo: 100% comissionado
- Base de cálculo: Valor líquido das vendas
- Período: Mensal

## Obrigações
1. Manter sigilo sobre informações da empresa
2. Não compartilhar dados de clientes
3. Seguir scripts e processos definidos
4. Reportar atividades diariamente

## Rescisão
- Aviso prévio de 15 dias
- Comissões pendentes serão pagas normalmente

## Declaro que li e aceito os termos acima
[ ] Aceito os termos do contrato`
  },
  {
    key: 'resources',
    label: 'Recursos',
    icon: FaLink,
    description: 'Links úteis, materiais de apoio e documentos.',
    placeholder: `# Recursos e Links Úteis

## Materiais de Vendas
- [Apresentação Institucional](link)
- [Catálogo de Produtos](link)
- [Cases de Sucesso](link)

## Documentos
- [Tabela de Preços](link)
- [Modelo de Proposta](link)
- [FAQ Comercial](link)

## Treinamentos
- [Vídeo: Como usar o CRM](link)
- [Vídeo: Técnicas de Vendas](link)
- [Vídeo: Conhecendo nossos produtos](link)

## Contatos Importantes
- Suporte: suporte@vegarobotics.com.br
- Comercial: comercial@vegarobotics.com.br
- WhatsApp Equipe: (XX) XXXXX-XXXX`
  },
  {
    key: 'faq',
    label: 'FAQ',
    icon: FaQuestionCircle,
    description: 'Perguntas frequentes e suas respostas.',
    placeholder: `# Perguntas Frequentes

## Sobre a função

**Como recebo minha comissão?**
Via transferência bancária ou PIX até o dia 5 do mês seguinte.

**Tenho horário fixo?**
Não, você tem flexibilidade de horário, mas esperamos dedicação mínima de X horas/semana.

**Preciso ir presencialmente?**
Não, o trabalho é 100% remoto.

## Sobre vendas

**Qual o ticket médio?**
Nossos projetos variam de R$ 3.000 a R$ 50.000.

**Como faço para tirar dúvidas técnicas?**
Acione o suporte técnico pelo canal do WhatsApp da equipe.

**Posso negociar descontos?**
Sim, até 10% de desconto com aprovação do gestor.`
  }
];

const JobScriptEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logActivity } = useAuth();
  const [job, setJob] = useState(null);
  const [script, setScript] = useState({
    overview: '',
    daily_routine: '',
    platform_guide: '',
    goals_metrics: '',
    contract_terms: '',
    resources: '',
    faq: ''
  });
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchJobAndScript();
  }, [id]);

  const fetchJobAndScript = async () => {
    try {
      // Buscar dados da vaga
      const { data: jobData, error: jobError } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', id)
        .single();

      if (jobError) throw jobError;
      setJob(jobData);

      // Buscar script existente
      const { data: scriptData, error: scriptError } = await supabase
        .from('job_scripts')
        .select('*')
        .eq('job_id', id)
        .single();

      if (scriptData) {
        setScript({
          overview: scriptData.overview || '',
          daily_routine: scriptData.daily_routine || '',
          platform_guide: scriptData.platform_guide || '',
          goals_metrics: scriptData.goals_metrics || '',
          contract_terms: scriptData.contract_terms || '',
          resources: scriptData.resources || '',
          faq: scriptData.faq || ''
        });
      }
    } catch (error) {
      console.error('Erro ao carregar:', error);
      if (error.code !== 'PGRST116') { // Not found é ok
        setError('Erro ao carregar dados da vaga');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);

    try {
      // Verificar se já existe um script
      const { data: existing } = await supabase
        .from('job_scripts')
        .select('id')
        .eq('job_id', id)
        .single();

      if (existing) {
        // Atualizar existente
        const { error } = await supabase
          .from('job_scripts')
          .update({
            ...script,
            last_updated_by: user?.email,
            updated_at: new Date().toISOString()
          })
          .eq('job_id', id);

        if (error) throw error;
      } else {
        // Criar novo
        const { error } = await supabase
          .from('job_scripts')
          .insert({
            job_id: id,
            ...script,
            last_updated_by: user?.email
          });

        if (error) throw error;
      }

      // Registrar atividade
      await logActivity('update_job_script', 'job_script', id, job?.title);

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar script. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setScript(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  if (loading) {
    return (
      <Container>
        <LoadingState>
          <FaSpinner /> Carregando...
        </LoadingState>
      </Container>
    );
  }

  if (error || !job) {
    return (
      <Container>
        <ErrorState>
          <h3>Vaga não encontrada</h3>
          <p>A vaga solicitada não existe ou foi removida.</p>
          <Button as={Link} to="/admin/vagas">
            <FaArrowLeft /> Voltar para Vagas
          </Button>
        </ErrorState>
      </Container>
    );
  }

  const activeSection = SECTIONS.find(s => s.key === activeTab);

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackLink to="/admin/vagas">
            <FaArrowLeft /> Voltar
          </BackLink>
          <div>
            <Title>Manual do Colaborador</Title>
            <JobTitle>{job.title}</JobTitle>
          </div>
        </HeaderLeft>
        <HeaderActions>
          {saved && (
            <SavedBadge>
              <FaCheck /> Salvo
            </SavedBadge>
          )}
          <Button as={Link} to={`/admin/vagas/${id}/script/preview`} target="_blank">
            <FaEye /> Visualizar
          </Button>
          <Button $primary onClick={handleSave} disabled={saving}>
            {saving ? <FaSpinner className="spin" /> : <FaSave />}
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </HeaderActions>
      </Header>

      <Card>
        <Tabs>
          {SECTIONS.map(section => (
            <Tab
              key={section.key}
              $active={activeTab === section.key}
              onClick={() => setActiveTab(section.key)}
            >
              <section.icon />
              {section.label}
            </Tab>
          ))}
        </Tabs>

        <TabContent>
          <SectionHeader>
            <activeSection.icon />
            <h2>{activeSection.label}</h2>
          </SectionHeader>
          <SectionDescription>
            {activeSection.description}
          </SectionDescription>

          <TextArea
            value={script[activeSection.key]}
            onChange={(e) => handleChange(activeSection.key, e.target.value)}
            placeholder={activeSection.placeholder}
          />

          <HelpText>
            Dica: Use Markdown para formatação. <code># Título</code>, <code>**negrito**</code>, <code>- lista</code>
          </HelpText>
        </TabContent>
      </Card>
    </Container>
  );
};

export default JobScriptEditor;
